import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import classNames from 'classnames';
import {
  DARK_THEME_PRODUCT_CARD_CLASSES,
  DARK_THEME_TEXT_CLASSES,
  getContainerBackground,
  LIGHT_THEME_PRODUCT_CARD_CLASSES,
  LIGHT_THEME_TEXT_CLASSES,
} from './constants';
import { AlignmentClasses, HeroBackgroundStyle, TextClasses } from './types';
import {
  CmsProduct,
  ComponentCtaButton,
  ComponentFlexibleHeroBlock,
  EnumFlexibleHeroBlockHeroType,
  EnumFlexibleHeroBlockTextAlign,
  EnumFlexibleHeroBlockTheme,
} from '$/(cms)/_modules/cmsService/types';
import { currencyFormat } from '$/components/cms/helpers/formatters';
import { PeriodGift } from '$/components/cms/types';

export const getRewardTitle = (gift: PeriodGift): string => {
  const value = gift.gift_value;
  if (!value?.price || !value?.prefix) {
    return gift.gift_title;
  }

  return `${currencyFormat(value.prefix)}${currencyFormatterNoDecimal.format(
    value.price,
  )}`;
};

export const getRewardSubtitle = (gift: PeriodGift): string | undefined => {
  const value = gift.gift_value;

  if (value?.topup_label && value.topup_amount && value.prefix) {
    return `${value.topup_label} ${currencyFormat(value.prefix)}${currencyFormatterNoDecimal.format(
      value.topup_amount,
    )}`;
  }

  return gift.additional_information;
};

export const normalizeButtons = (
  primary?: ComponentCtaButton,
  secondary?: ComponentCtaButton,
): ComponentCtaButton[] => {
  const buttons: ComponentCtaButton[] = [];

  if (primary?.label && primary?.visible !== false) {
    buttons.push(primary);
  }

  if (secondary?.label && secondary?.visible !== false) {
    buttons.push(secondary);
  }

  return buttons;
};

export const getHeroBackground = (
  theme: EnumFlexibleHeroBlockTheme | undefined,
  backgroundImageDesktop: ComponentFlexibleHeroBlock['backgroundImageDesktop'],
  backgroundImageMobile: ComponentFlexibleHeroBlock['backgroundImageMobile'],
): {
  style: HeroBackgroundStyle;
  className: string;
} => {
  const fallback = getContainerBackground(theme);
  const desktopUrl = backgroundImageDesktop?.url ?? fallback;
  const mobileUrl =
    backgroundImageMobile?.url ?? backgroundImageDesktop?.url ?? desktopUrl;

  const style: HeroBackgroundStyle = {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  if (mobileUrl) {
    style['--flexible-hero-bg-mobile'] = `url(${mobileUrl})`;
  }

  if (desktopUrl) {
    style['--flexible-hero-bg-desktop'] = `url(${desktopUrl})`;
  }

  return {
    style,
    className:
      'bg-cover bg-no-repeat bg-center bg-[image:var(--flexible-hero-bg-mobile)] md:bg-[image:var(--flexible-hero-bg-desktop)]',
  };
};

export const getTextClasses = (
  theme: EnumFlexibleHeroBlockTheme | undefined,
): TextClasses =>
  theme === EnumFlexibleHeroBlockTheme.Dark
    ? DARK_THEME_TEXT_CLASSES
    : LIGHT_THEME_TEXT_CLASSES;

export const getProductCardClasses = (
  theme: EnumFlexibleHeroBlockTheme | undefined,
): string =>
  theme === EnumFlexibleHeroBlockTheme.Dark
    ? DARK_THEME_PRODUCT_CARD_CLASSES
    : LIGHT_THEME_PRODUCT_CARD_CLASSES;

export const getAlignmentClasses = (
  textAlign: ComponentFlexibleHeroBlock['textAlign'],
): AlignmentClasses => {
  const isCenter = textAlign === EnumFlexibleHeroBlockTextAlign.Center;

  return {
    container: classNames('flex flex-col gap-4', {
      'items-center text-center': isCenter,
      'items-start text-left': !isCenter,
    }),
    ctaWrapper: classNames('flex w-full flex-col gap-3 md:flex-row md:gap-4', {
      'md:justify-center md:text-center': isCenter,
      'md:justify-start': !isCenter,
    }),
    isCenter,
  };
};

/**
 * A hero CTA exists only when it has a URL (CMS-1966); never gate on the
 * label, which always resolves through the apply fallback.
 */
export const getCmsCallToAction = (
  callToAction:
    | ComponentFlexibleHeroBlock['productCallToAction']
    | ComponentFlexibleHeroBlock['providerCallToAction'],
  fallbackLabel: string,
): ComponentCtaButton | undefined => {
  const link = callToAction?.links?.[0];

  if (!link?.redirectedUrl) {
    return undefined;
  }

  return {
    id: '0',
    __typename: 'ComponentPagesComponentCtaButton',
    label: link.label || fallbackLabel,
    link: link.redirectedUrl,
  };
};

/** The link a hero's authored Product CTA sets in place of the product's own, if any. */
export const getProductCtaLinkOverride = (
  productCallToAction: ComponentFlexibleHeroBlock['productCallToAction'],
): string | undefined => getCmsCallToAction(productCallToAction, '')?.link;

/**
 * The apply link a hero's Product CTA sends its readers to, when the hero renders one: only a
 * Product hero does — Strapi hides the field for the other types, but a value set before the type
 * changed stays in it. Callers hand it a rendered (visible) hero.
 */
export const getProductHeroCtaLinkOverride = (
  hero: ComponentFlexibleHeroBlock | undefined,
): string | undefined =>
  hero?.heroType === EnumFlexibleHeroBlockHeroType.Product
    ? getProductCtaLinkOverride(hero.productCallToAction)
    : undefined;

/**
 * The hero's Product CTA link for another CTA of `product` on the same page that has to go where
 * the hero goes (the PDP sticky footer, CMS-2043) — only when the hero shows that very product.
 */
export const getHeroCtaLinkOverrideFor = (
  hero: ComponentFlexibleHeroBlock | undefined,
  product: Pick<CmsProduct, 'uid'> | undefined,
): string | undefined =>
  product?.uid && hero?.product?.uid === product.uid
    ? getProductHeroCtaLinkOverride(hero)
    : undefined;

/**
 * `productHref` is the product's resolved apply URL (`useCtaUrl({ product, ctaLinkOverride })`);
 * without it only the block's own CTA shows (CMS-1966). A host link (`hostCtaLinkOverride`, e.g. the
 * card a `#usePDP` modal opened from) outranks the block's authored link: its resolved URL becomes
 * the destination, while the editor's label stays (CMS-2043).
 */
export const getProductCallToAction = (
  product: ComponentFlexibleHeroBlock['product'],
  override: ComponentFlexibleHeroBlock['productCallToAction'],
  fallbackLabel: string,
  productHref: string | undefined,
  hostCtaLinkOverride?: string,
): ComponentCtaButton | undefined => {
  const overrideCta = getCmsCallToAction(override, fallbackLabel);
  if (overrideCta) {
    return hostCtaLinkOverride && productHref
      ? { ...overrideCta, link: productHref }
      : overrideCta;
  }

  if (!productHref) {
    return undefined;
  }

  const productCta = product?.call_to_action;
  const label =
    productCta?.links?.[0]?.label ?? productCta?.title ?? fallbackLabel;

  return {
    id: '0',
    __typename: 'ComponentPagesComponentCtaButton',
    label: label || fallbackLabel,
    link: productHref,
  };
};
